/**
 * Catalog: products, the product editor, variants, images, CSV import and
 * categories. Conventions: see `common.ts`.
 */
const catalog: Record<string, string> = {
  uncategorised: 'Uncategorised',
  loading_catalog: 'Loading your catalog…',

  'status.ACTIVE': 'Active',
  'status.DRAFT': 'Draft',
  'status.ARCHIVED': 'Archived',

  'sort.newest': 'Newest first',
  'sort.oldest': 'Oldest first',
  'sort.name_asc': 'Name A–Z',
  'sort.name_desc': 'Name Z–A',
  'sort.price_asc': 'Price low to high',
  'sort.price_desc': 'Price high to low',

  'columns.product': 'Product',
  'columns.price': 'Price',
  'columns.inventory': 'Inventory',
  'columns.status': 'Status',
  'columns.category': 'Category',
  'columns.type': 'Type',
  'columns.brand': 'Brand',
  'columns.sku': 'SKU',
  'columns.tags': 'Tags',
  'columns.created': 'Date added',
  'columns.action': 'Action',

  // ---- products list ----
  'products.title': 'Products',
  'products.total_in_catalog': '{total} in your catalog',
  'products.exporting': 'Exporting…',
  'products.add_product': 'Add product',
  'products.add_first': 'Add your first product',
  'products.empty_title': 'No products yet',
  'products.empty_description': 'Add your first product, or let AI draft the copy for you.',
  'products.empty_search_title': 'No products match that search',
  'products.empty_search_description': 'Try a different name or brand.',
  'products.select_all': 'Select all products on this page',
  'products.select_product': 'Select {name}',
  'products.show_in_store': 'Show {name} in your store',
  'products.stock.unlimited': 'Unlimited',
  'products.stock.out_of_stock': 'Out of stock',
  'products.stock.in_stock': '{quantity} in stock',
  'products.errors.update_failed': 'Could not update those products.',

  'toolbar.search_label': 'Search products',
  'toolbar.search_placeholder': 'Search by name, brand or type',
  'toolbar.confirm_delete_one':
    'Delete {count} product? They leave your store but stay on past orders.',
  'toolbar.confirm_delete_other':
    'Delete {count} products? They leave your store but stay on past orders.',
  'toolbar.selected': '{count} Selected',
  'toolbar.add_to_category': 'Add to category',
  'toolbar.no_categories': 'No categories yet.',
  'toolbar.hide': 'Hide',
  'toolbar.sort_by': 'Sort by',
  'toolbar.filter': 'Filter',
  'toolbar.all_statuses': 'All statuses',
  'toolbar.all_categories': 'All categories',
  'toolbar.clear_filters': 'Clear filters',
  'toolbar.choose_columns': 'Choose columns',
  'toolbar.columns': 'Columns',
  'toolbar.reset_columns': 'Reset to default',

  'row_actions.preview': 'Preview in your store',
  'row_actions.preview_unavailable': 'Only active products appear in your store',
  'row_actions.preview_label': 'Preview {name} in your store',
  'row_actions.copy_link': 'Copy store link',
  'row_actions.copy_link_label': 'Copy store link for {name}',
  'row_actions.link_copied': 'Link copied',
  'row_actions.more': 'More actions for {name}',
  'row_actions.edit': 'Edit details',
  'row_actions.hide': 'Hide from store',
  'row_actions.publish': 'Publish to store',
  'row_actions.restore': 'Restore as draft',

  // ---- CSV import ----
  'import.title': 'Import products',
  'import.summary_new_one': '{count} new',
  'import.summary_new_other': '{count} new',
  'import.summary_updated_one': '{count} updated',
  'import.summary_updated_other': '{count} updated',
  'import.summary_skipped_one': '{count} skipped',
  'import.summary_skipped_other': '{count} skipped',
  'import.row': 'Row {row}:',
  'import.finished': 'Import finished: {summary}.',
  'import.drop_prompt': 'Drop a CSV here, or click to choose a file',
  'import.file_label': 'Product CSV file',
  'import.help':
    'One row per variant. Rows update existing products by handle and variants by SKU; ' +
    'anything else is created. Exported files import back unchanged.',
  'import.download_sample': 'Download a sample CSV',
  'import.checking': 'Checking the file…',
  'import.preview_one': 'This file has {count} row: {summary}. Nothing has been saved yet.',
  'import.preview_other': 'This file has {count} rows: {summary}. Nothing has been saved yet.',
  'import.importing': 'Importing…',
  'import.errors.read_failed': 'Could not read that file.',

  // ---- images ----
  'images.select_from_library': 'Select from Media Library',
  'images.paste_link': 'Or paste an image link',
  'images.invalid_link': 'Use a full link starting with http:// or https://',
  'images.opening_studio': 'Opening AI Image Studio…',

  'drop_zone.drop_single': '{action} or drop it here',
  'drop_zone.drop_multiple': '{action} or drop them here',
  'drop_zone.or_drop_single': 'Or drop an image here',
  'drop_zone.or_drop_multiple': 'Or drop images here',
  'drop_zone.formats': 'JPEG, PNG, WebP, GIF or AVIF, up to 5 MB each. Saved as WebP.',
  'drop_zone.uploading_one': 'Uploading and resizing {count} image…',
  'drop_zone.uploading_other': 'Uploading and resizing {count} images…',

  'image_field.attached': 'Image attached',
  'image_field.edit': 'Edit {label}',
  'image_field.remove': 'Remove {label}',
  'image_field.choose': 'Choose {label}',
  'image_field.reposition': 'Reposition {label}',

  'media.alt_placeholder': 'Alt text, for screen readers and SEO',
  'media.alt_label': 'Alt text for image {number}',
  'media.edit_image': 'Edit image {number}',
  'media.reposition_image': 'Choose what stays in view for image {number}',
  'media.frame_square': 'Square - product page, cards and thumbnails',
  'media.frame_portrait': 'Tall (4:5) - product page on some themes',
  'media.frame_wide': 'Wide (16:9) - product page on some themes',
  'media.move_earlier': 'Move image {number} earlier',
  'media.move_later': 'Move image {number} later',
  'media.remove_image': 'Remove image {number}',
  'media.uploading': 'Uploading and resizing…',
  'media.empty': 'No images yet. The first one becomes the listing thumbnail.',
  'media.ai_subject': 'product image',
  'media.library_title': 'Add images from your Media Library',

  'image_edit.title': 'Edit image',
  'image_edit.reading': 'Reading the image…',
  'image_edit.load_failed':
    'This image could not be loaded for editing - its host may not allow it. ' +
    'Upload the file here instead of linking it to make it editable.',
  'image_edit.invalid_size': 'Enter a size between 1 and {max} pixels.',
  'image_edit.edit_failed': 'Could not edit that image.',
  'image_edit.tool_label': 'Editing tool',
  // Lower case: the tab styles capitalise the first letter themselves.
  'image_edit.tab.crop': 'crop',
  'image_edit.tab.resize': 'resize',
  'image_edit.ratio': 'Ratio',
  'image_edit.aspect_free': 'Free',
  'image_edit.crop_summary':
    'Crop to {width} × {height} px, from {original_width} × {original_height}',
  'image_edit.original': 'Original: {width} × {height} px',
  'image_edit.width': 'Width (px)',
  'image_edit.height': 'Height (px)',
  'image_edit.left': 'Left (px)',
  'image_edit.top': 'Top (px)',
  'image_edit.save': 'Save image',

  'tags.remove': 'Remove tag {tag}',

  // ---- variants ----
  'variants.option_name': 'Option name',
  'variants.option_placeholder_size': 'Size',
  'variants.option_placeholder_colour': 'Colour',
  'variants.values': 'Values',
  'variants.values_placeholder_size': 'S, M, L',
  'variants.values_placeholder_colour': 'Red, Blue',
  'variants.remove_option': 'Remove option {name}',
  'variants.add_options': 'Add options like size or colour',
  'variants.add_another_option': 'Add another option',
  'variants.too_many_variants':
    'These options make {total} combinations; the most a product can hold is {max}. ' +
    'Remove some values.',
  'variants.variant': 'Variant',
  'variants.sku_for': 'SKU for {label}',
  'variants.sku_auto': 'Auto',
  'variants.price_for': 'Price for {label}',
  'variants.price_placeholder': 'Product price',
  'variants.stock_for': 'Opening stock for {label}',
  'variants.will_create_one':
    '{count} variant will be created. Blank SKUs are filled in from the product name.',
  'variants.will_create_other':
    '{count} variants will be created. Blank SKUs are filled in from the product name.',
  'variants.option_of_variant': '{option} of variant {sku}',
  'variants.default': 'Default',
  'variants.sku_of_variant': 'SKU of variant {sku}',
  'variants.price_of_variant': 'Price of variant {sku}',
  'variants.delete_variant': 'Delete variant {sku}',
  'variants.minimum_variant': 'A product must keep at least one variant.',
  'variants.manager_help':
    'Blank prices inherit the product price. Rows save on their own, not with the form. ' +
    'Per-variant stock lives in Inventory.',
  'variants.first_value': 'First {option}',
  'variants.value_fallback': 'value',
  'variants.tip_existing':
    'Tip: after adding, give the existing variant its own {option} value in the table above.',
  'variants.option_fallback': 'option',
  'variants.adding': 'Adding…',
  'variants.add_variant': 'Add variant',
  'variants.price_overrides': 'Priced overrides: {list}',
  'variants.errors.sku_required': 'Every variant needs a SKU.',
  'variants.errors.save_failed': 'Could not save that variant.',
  'variants.errors.delete_failed': 'Could not delete that variant.',
  'variants.errors.option_name_required': 'Name the option first - Size, Colour…',
  'variants.errors.value_required': 'Give the new variant a value for at least one option.',
  'variants.errors.new_sku_required': 'Give the new variant a SKU.',
  'variants.errors.add_failed': 'Could not add that variant.',

  // ---- product editor ----
  'product_form.back': 'Back to products',
  'product_form.new_title': 'New product',
  'product_form.edit_title': 'Edit product',
  'product_form.create': 'Create product',
  // A service business's form hides the sections for goods that ship; this
  // brings them back for the clinic that also sells a product.
  'product_form.physical.show': 'Show shipping, returns & inventory options',
  'product_form.physical.hide': 'Hide shipping, returns & inventory options',
  'product_form.read_only': 'You have read-only access to products.',
  'product_form.archived_notice':
    'This product is archived. Saving brings it back as a draft or active listing.',
  'product_form.profit': 'Profit',
  'product_form.margin': 'Margin',
  'product_form.make_image_with_ai': 'Make a product image with AI',
  'product_form.errors.load_failed': 'Could not load that product.',
  'product_form.errors.save_failed': 'Could not save. Please try again.',

  'product_form.ai.draft': 'Draft with AI',
  'product_form.ai.writing': 'Writing…',
  'product_form.ai.name_first': 'Enter a product name first.',
  'product_form.ai.applied': 'Draft applied below - review before saving.',
  'product_form.ai.applied_remaining':
    'Draft applied below - review before saving. {remaining} generations left this month.',
  'product_form.ai.unreachable': 'The AI service could not be reached.',
  'product_form.ai.streaming': 'Writing the description and SEO text…',
  'product_form.ai.stop': 'Stop',
  'product_form.ai.stopped': 'Stopped. What the AI had written so far is kept.',
  'product_form.ai.restore': 'Restore previous text',

  'product_form.sections.details': 'Details',
  'product_form.sections.details_description': 'What shoppers read on the product page.',
  'product_form.sections.media': 'Media',
  'product_form.sections.media_description':
    'The first image is used for listings and search results. 1600 × 1600 px (square) is ideal—it ' +
    'matches the storefront’s widest image size and avoids unnecessary scaling or cropping.',
  'product_form.sections.pricing': 'Pricing',
  'product_form.sections.inventory': 'Inventory',
  'product_form.sections.inventory_description': 'Identifiers and stock for this product.',
  'product_form.sections.inventory_variants':
    'This product has variants; stock is tracked per variant.',
  'product_form.sections.shipping': 'Shipping',
  'product_form.sections.variants': 'Variants',
  'product_form.sections.variants_edit_description':
    'Options like size or colour, each with its own SKU and price.',
  'product_form.sections.variants_create_description':
    'Sell options like size or colour, each with its own SKU, price and stock.',
  'product_form.sections.metafields': 'Metafields',
  'product_form.sections.metafields_description':
    'The custom fields you defined under Content. Ones marked available on the storefront ship ' +
    'with the product.',
  'product_form.sections.seo': 'Search engine listing',
  'product_form.sections.seo_description':
    'How the product reads on Google. Left blank, the title and description are used.',
  'product_form.sections.status': 'Status',
  'product_form.sections.organisation': 'Product organisation',
  'product_form.sections.organisation_description':
    'How you find it later, and how shoppers browse to it.',

  'product_form.fields.title': 'Title',
  'product_form.fields.title_placeholder': 'Oversized cotton T-shirt',
  'product_form.fields.short_description': 'Short description',
  'product_form.fields.short_description_placeholder': 'One line shoppers see in listings',
  'product_form.fields.description': 'Description',
  'product_form.fields.description_placeholder': 'What makes this product worth buying?',
  'product_form.fields.price': 'Price (₹)',
  'product_form.fields.compare_at_price': 'Compare-at price (₹)',
  'product_form.fields.compare_at_price_hint': 'Shown struck through',
  'product_form.fields.cost_price': 'Cost per item (₹)',
  'product_form.fields.cost_price_hint': 'Never shown to shoppers',
  'product_form.fields.gst_rate': 'GST rate (%)',
  'product_form.fields.hsn_code': 'HSN / SAC code',
  'product_form.fields.tax_inclusive': 'Price includes GST',
  'product_form.fields.tax_inclusive_on': 'Tax is worked back out of the price on invoices.',
  'product_form.fields.tax_inclusive_off': 'GST is added on top at checkout.',
  'product_form.fields.track_inventory': 'Track quantity',
  'product_form.fields.sku': 'SKU',
  'product_form.fields.sku_hint': 'Defaults to the product name if left blank.',
  'product_form.fields.barcode': 'Barcode (ISBN, UPC, GTIN)',
  'product_form.fields.opening_stock': 'Opening stock',
  'product_form.fields.opening_stock_hint':
    'Creates the inventory record. Adjust it any time from Inventory.',
  'product_form.fields.low_stock_threshold': 'Low stock alert at',
  'product_form.fields.low_stock_threshold_hint':
    'Below this, the product shows up in the low-stock report.',
  'product_form.fields.weight': 'Weight (grams)',
  'product_form.fields.weight_hint': 'Used to rate shipping at checkout.',
  'product_form.fields.seo_title': 'SEO title',
  'product_form.fields.seo_description': 'SEO description',
  'product_form.fields.status': 'Status',
  'product_form.fields.status_draft': 'Draft - not visible to shoppers',
  'product_form.fields.status_active': 'Active - live on your store',
  'product_form.fields.product_type': 'Type',
  'product_form.fields.product_type_placeholder': 'T-shirt',
  'product_form.fields.brand': 'Vendor / brand',
  'product_form.fields.brand_placeholder': 'Your label',
  'product_form.fields.category': 'Category',
  'product_form.fields.tags': 'Tags',
  'product_form.fields.tags_placeholder': 'summer, cotton',

  'product_form.inventory.draft_variants':
    'SKUs and opening stock are set per variant in the Variants section below.',
  'product_form.inventory.adjust_per_variant': "Adjust each variant's stock from {link}.",
  'product_form.inventory.inventory_link': 'Inventory',
  'product_form.inventory.untracked':
    'Stock is not counted for this product - it can always be bought.',
  'product_form.inventory.on_hand': 'On hand',
  'product_form.inventory.committed': 'Committed',
  'product_form.inventory.available': 'Available',
  'product_form.inventory.movements': 'Stock changes are recorded as movements. {link}.',
  'product_form.inventory.adjust_link': 'Adjust stock and view history',

  'validation.after_sale_window': 'Enter whole days from 1 to 365, or leave it empty.',
  'validation.name_required': 'Give the product a name.',
  'validation.price_required': 'Enter a price.',
  'validation.price_format': 'Use a number like 799 or 799.50',
  'validation.compare_at_format': 'Use a number like 999 or 999.50',
  'validation.cost_format': 'Use a number like 400 or 400.50',
  'validation.gst_max': 'GST cannot exceed 28%.',
  'validation.hsn_numeric': 'HSN codes are numeric.',
  'validation.stock_whole': 'Stock is a whole number.',
  'validation.whole_number': 'Use a whole number.',
  'validation.weight_whole': 'Weight is a whole number of grams.',
  'validation.compare_at_above_price': 'The compare-at price has to be above the selling price.',
  'validation.variant_price_format': 'The price for {label} is not a number like 799 or 799.50.',
  'validation.variant_stock_whole': 'Opening stock for {label} is a whole number.',
  'validation.duplicate_sku': 'Two variants ended up with the SKU {sku}. Give them distinct SKUs.',

  // ---- categories ----
  'categories.title': 'Categories',
  'categories.count_one': '{count} category',
  'categories.count_other': '{count} categories',
  'categories.add': 'Add category',
  'categories.create_first': 'Create your first category',
  'categories.empty_title': 'No categories yet',
  'categories.empty_description':
    'Group your products so shoppers can browse them - clothing, footwear, offers.',
  'categories.confirm_delete': 'Delete {name}? Its products stay, without a category.',
  'categories.edit_name': 'Edit {name}',
  'categories.delete_name': 'Delete {name}',
  'categories.errors.delete_failed': 'Could not delete that category.',
  'categories.search_placeholder': 'Search categories…',
  'categories.select_row': 'Select {name}',
  'categories.sub_count_one': '{count} sub-category',
  'categories.sub_count_other': '{count} sub-categories',

  'categories.columns.name': 'Category',
  'categories.columns.products': 'Products',
  'categories.columns.active': 'Live',
  'categories.columns.parent': 'Parent',
  'categories.columns.slug': 'Web address',
  'categories.columns.position': 'Order',
  'categories.columns.description': 'Description',

  'categories.view.tree': 'Tree view',
  'categories.view.list': 'List view',

  'categories.sort.position': 'Shop order',
  'categories.sort.products_desc': 'Most products',
  'categories.sort.products_asc': 'Fewest products',

  'categories.filters.products': 'Products',
  'categories.filters.with_products': 'Has products',
  'categories.filters.without_products': 'Empty',
  'categories.filters.no_active_products': 'Nothing live',
  'categories.filters.level': 'Level',
  'categories.filters.top_level': 'Top level',
  'categories.filters.sub_category': 'Sub-category',
  'categories.filters.any_parent': 'Any parent',
  'categories.filters.status': 'Status',
  'categories.filters.status_active': 'Active',
  'categories.filters.status_inactive': 'Inactive',

  'categories.status.inactive': 'Inactive',
  'categories.status.inactive_hint': 'Hidden from your shop, with its sub-categories',
  'categories.activate': 'Activate',
  'categories.deactivate': 'Deactivate',
  'categories.activate_name': 'Activate {name}',
  'categories.deactivate_name': 'Deactivate {name}',
  'categories.errors.status_failed': 'Could not change that category.',

  'categories.kpi.total': 'Categories',
  'categories.kpi.top_level': 'Top level',
  'categories.kpi.top_level_hint': 'Shown in your shop menu',
  'categories.kpi.with_products': 'With products',
  'categories.kpi.empty': 'Empty',
  'categories.kpi.empty_hint': 'Shoppers land on a blank page',

  'categories.bulk.move_under': 'Move under',
  'categories.bulk.delete_title': 'Delete categories',
  'categories.bulk.delete_message_one':
    'Delete {count} category? Its products stay, without a category.',
  'categories.bulk.delete_message_other':
    'Delete {count} categories? Their products stay, without a category.',

  'categories.toast.created': '{name} created.',
  'categories.toast.updated': '{name} saved.',
  'categories.toast.deleted': 'Category deleted.',
  'categories.toast.bulk_deleted_one': '{count} category deleted.',
  'categories.toast.bulk_deleted_other': '{count} categories deleted.',
  'categories.toast.bulk_moved_one': '{count} category moved.',
  'categories.toast.bulk_moved_other': '{count} categories moved.',
  'categories.toast.activated': '{name} is back in your shop.',
  'categories.toast.deactivated': '{name} is hidden from your shop.',
  'categories.toast.bulk_activated_one': '{count} category activated.',
  'categories.toast.bulk_activated_other': '{count} categories activated.',
  'categories.toast.bulk_deactivated_one': '{count} category deactivated.',
  'categories.toast.bulk_deactivated_other': '{count} categories deactivated.',

  'category_form.new_title': 'New category',
  'category_form.description':
    'Categories group products for your shoppers and drive the filters in your storefront.',
  'category_form.name': 'Name',
  'category_form.name_placeholder': 'T-Shirts',
  'category_form.slug': 'Web address',
  'category_form.slug_hint': 'Leave blank to build one from the name.',
  'category_form.parent': 'Parent category',
  'category_form.no_parent': 'No parent (top level)',
  'category_form.position': 'Sort order',
  'category_form.position_hint': 'Lower numbers appear first.',
  'category_form.active': 'Active',
  'category_form.active_hint': 'Off hides it and its sub-categories from your shop. Their products stay on sale.',
  'category_form.image': 'Category image',
  'category_form.image_hint':
    'Shown on category tiles in your storefront. Larger uploads are only scaled back down.',
  'category_form.frame_tile': 'Category tile with the name on it',
  'category_form.frame_icon': 'Round icon in the header',
  'category_form.description_label': 'Description',
  'category_form.description_placeholder': 'What belongs in this category?',
  'category_form.create': 'Create category',
  'category_form.errors.save_failed': 'Could not save this category.',

  // "Add multiple": many categories at once, typed in or from the library.
  'categories.bulk_add.open': 'Add multiple',
  'categories.bulk_add.title': 'Add multiple categories',
  'categories.bulk_add.paste_label': 'Paste category names',
  'categories.bulk_add.paste_placeholder': 'Shirts, Kurtas, Sarees',
  'categories.bulk_add.paste_hint': 'Separate names with commas or new lines.',
  'categories.bulk_add.paste_add': 'Add to list',
  'categories.bulk_add.library_industry': 'Suggestions for',
  'categories.bulk_add.library_general': 'General',
  'categories.bulk_add.library_title': 'Suggested categories',
  'categories.bulk_add.library_loading': 'Loading suggestions…',
  'categories.bulk_add.library_error': 'Suggestions could not be loaded.',
  'categories.bulk_add.library_empty': 'No suggestions for this industry yet.',
  'categories.bulk_add.library_in_store': 'Already in your store',
  'categories.bulk_add.left_out': 'Already in your store or on the list: {names}',
  'categories.bulk_add.batch_title_one': 'To create: {count} of {max}',
  'categories.bulk_add.batch_title_other': 'To create: {count} of {max}',
  'categories.bulk_add.batch_empty': 'Paste names or pick suggestions to build your list.',
  'categories.bulk_add.name': 'Name',
  'categories.bulk_add.parent': 'Parent',
  'categories.bulk_add.row_name': 'Name of category {number}',
  'categories.bulk_add.row_parent': 'Parent of category {number}',
  'categories.bulk_add.remove_row': 'Remove {name}',
  'categories.bulk_add.save_one': 'Create {count} category',
  'categories.bulk_add.save_other': 'Create {count} categories',
  'categories.bulk_add.done': 'Done',
  'categories.bulk_add.toast_created': 'Categories created: {created}.',
  'categories.bulk_add.toast_created_skipped': 'Categories created: {created}. Skipped: {skipped}.',
  'categories.bulk_add.skipped_title': 'These were not created:',
  'categories.bulk_add.reason_exists': 'already in your store',
  'categories.bulk_add.reason_duplicate': 'repeated in the list',
  'categories.bulk_add.errors.limit': 'You can add up to {max} categories at a time.',
  'categories.bulk_add.errors.too_long': 'Use {max} characters or fewer.',
  'categories.bulk_add.errors.save_failed': 'Categories could not be created.',

  // Sample products seeded at signup.
  'products.samples.badge': 'Sample',
  'products.samples.remove': 'Remove sample content',
  'products.samples.confirm_title': 'Remove sample content?',
  'products.samples.confirm_message_one':
    'This deletes the {count} sample product added when your store was created, along with the sample team members, locations and blog posts. Samples you have edited are kept.',
  'products.samples.confirm_message_other':
    'This deletes the {count} sample products added when your store was created, along with the sample team members, locations and blog posts. Samples you have edited are kept.',
  'products.samples.confirm_message_content':
    'This deletes the sample team members, locations and blog posts added when your store was created. Samples you have edited are kept.',
  'products.samples.confirm': 'Remove samples',
  'products.samples.removed_one': '{count} sample item removed.',
  'products.samples.removed_other': '{count} sample items removed.',
  'products.samples.remove_failed': 'Sample content could not be removed.',
  'products.samples.load': 'Load sample products',
  'products.samples.loading': 'Adding samples…',
  'products.samples.load_hint':
    'Adds 5 draft products for your industry, marked Sample. Edit them into your own, or remove them anytime.',
  'products.samples.loaded_one': '{count} sample product added.',
  'products.samples.loaded_other': '{count} sample products added.',
  'products.samples.load_failed': 'Sample products could not be added.',
  'product_form.sections.bundle': 'Frequently bought together',
  'product_form.sections.bundle_description':
    'Up to three products to sell with this one, and what customers save when they buy the whole set.',
  'bundle.products': 'Products to bundle',
  'bundle.products_hint': 'Shown after this product on its page, in this order. Out-of-stock and draft products are hidden there.',
  'bundle.add_products': 'Choose products',
  'bundle.full': 'Three products chosen',
  'bundle.remove': 'Remove {name}',
  'bundle.discount_type': 'Bundle discount',
  'bundle.discount_none': 'No discount',
  'bundle.discount_percent': 'Percent off the set',
  'bundle.discount_fixed': 'Amount off each set',
  'bundle.value_percent': 'Percent off',
  'bundle.value_fixed': 'Amount off (₹)',
  'bundle.active': 'Show the bundle',
  'bundle.theme_note':
    'Shoppers see it where your theme has the "Frequently bought together" component on the product page - add it in the theme customiser.',
  'bundle.save': 'Save bundle',
  'bundle.saved': 'Bundle saved.',
  'bundle.save_error': 'The bundle could not be saved.',
  'bundle.load_error': 'The bundle could not be loaded.',
  'bundle.errors.value_required': 'Enter how much the bundle takes off, or choose No discount.',
  'bundle.errors.percent_max': 'A bundle can take at most 90% off.',

  // Adding from the platform's product master.
  'master.open': 'Add from catalog',
  'master.title': 'Add products from the catalog',
  'master.description': 'Pick everyday products with their name, photos, brand, barcode and GST already filled in. Set your price, and they are added to your store as your own products.',
  'master.search_placeholder': 'Search by name, brand or barcode',
  'master.industry_scope': 'Showing products for {industry} stores.',
  'master.categories': 'Categories',
  'master.all_categories': 'All',
  'master.mrp': 'MRP {price}',
  'master.price': 'Price',
  'master.price_for': 'Your price for {name}',
  'master.already_added': 'In your store',
  'master.as_draft': 'Add as drafts (hidden until you publish)',
  'master.selected': '{count} selected',
  'master.add': 'Add {count}',
  'master.adding': 'Adding…',
  'master.added_one': '{count} product added to your store.',
  'master.added_other': '{count} products added to your store.',
  'master.skipped_one': '{count} product was not added:',
  'master.skipped_other': '{count} products were not added:',
  'master.add_failed': 'Could not add the products.',
  'master.load_failed': 'Could not load the catalog.',
  'master.empty_title': 'No products found',
  'master.empty_description': 'Try another search or category. Only products for your store’s industry are listed.',
  'master.lookup': 'Find by barcode',
  'master.lookup_hint': 'Fill this form from the catalog: type or scan the barcode on the pack.',
  'master.scan': 'Scan',
  'master.lookup_found': 'Filled in from the catalog: {name}. Check the details and set your price.',
  'master.lookup_missing': 'No catalog product has that barcode. Fill in the details yourself.',
  'master.lookup_already': '{name} is already in your store.',
  'master.scan_unsupported': 'This browser cannot scan barcodes. Type the number instead.',
  'master.scan_title': 'Scan a barcode',
  'master.scan_hint': 'Point the camera at the barcode on the pack.',
};

export default catalog;
